import { useMemo } from 'react'

import {
  formatDate,
  matchFilterBetweenDates, matchFilterNumber, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { CreditNoteModel, CreditNoteRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['creditNotes']

type CreditNoteFilters = {
  agency_id?: number,
  campaign_id?: number,
  end_date?: string,
  start_date?: string,
  string?: string,
}

type UseCreditNotesOptions = {
  filters?: CreditNoteFilters,
  performHttpRequests?: boolean,
  requestOptions?: CreditNoteRequestOptions,
}

function useCreditNotes(options: UseCreditNotesOptions) {
  const { filters = {}, requestOptions } = options
  const {
    agency_id: filterAgencyId,
    campaign_id: filterCampaignId,
    end_date: filterCreatedBefore,
    start_date: filterCreatedAfter,
    string: filterString,
  } = filters

  const {
    updatedEntities: { creditNotes: creditNotesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { campaigns, creditNotes } = useSelector(reduxState => reduxState.entities)

  const filteredCreditNotes = useMemo(() => {
    const filtered = Object.values(creditNotes).filter((creditNote: CreditNoteModel) => {
      const {
        campaign_id, reference, dates,
      } = creditNote

      const { agency_id } = campaigns[campaign_id!] || {}
      const createdAt = formatDate(dates?.created?.date_time, 'dd/MM/yyyy HH:mm')

      const matchAgency = matchFilterNumber(agency_id, filterAgencyId)
      const matchCampaign = matchFilterNumber(campaign_id, filterCampaignId)
      const matchReference = matchFilterString(reference, filterString?.toString())
      const matchCreatedAt = matchFilterBetweenDates(createdAt, filterCreatedAfter, filterCreatedBefore)

      return matchAgency && matchReference && matchCampaign && matchCreatedAt
    })

    return sortArrayBy(filtered, 'desc', 'id')
  }, [creditNotesUpdatedAt, JSON.stringify(filters)])

  const filteredCreditNotesCount = filteredCreditNotes.length
  const hasFilteredCreditNotes = !!filteredCreditNotesCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredCreditNotesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingCreditNotes } = useReduxAction(
    'creditNotes',
    'loadCreditNotes',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredCreditNotes,
    filteredCreditNotesCount,
    hasFilteredCreditNotes,
    loading: loadingCreditNotes,
  }
}

export default useCreditNotes
